
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchExtratos } from './api/extratos';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import { fetchContasByEscolaAndPrograma } from './api/contas';
import usePagination10 from './components/hooks/usePagination10';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useReferenceData from './components/hooks/useReferenceData';
import ExtratoModal from './components/modals/ExtratoModal';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatDateView, toIsoDate } from './utils/dateUtils';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  id: '', ano: '', idPrograma: '', idEscola: '', idConta: '', tipo: '', documento: '', vlr: '', data: ''
};

function keyOf(item) {
  return `${item.id}_${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}_${item.tipo}_${item.documento}_${item.data}`;
}

function formatCurrency(value) {
  if (value == null) return '';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}


export default function Extratos() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas } = useReferenceData(token, cnpj);
  const [contas, setContas] = useState([]);
    // Carrega contas ao abrir tela ou ao mudar cnpj/ano
    useEffect(() => {
      if (!token || !cnpj) { setContas([]); return; }
      // Se quiser filtrar por programa, pode passar idPrograma
      fetchContasByEscolaAndPrograma({ token, cnpj, idPrograma: undefined })
        .then(data => {
          // Ordena por número da conta
          function parseContaNum(numConta) {
            const m = String(numConta).match(/^(\d+)/);
            return m ? parseInt(m[1], 10) : 0;
          }
          setContas([...data].sort((a, b) => {
            const nA = parseContaNum(a.numConta);
            const nB = parseContaNum(b.numConta);
            if (nA !== nB) return nA - nB;
            return String(a.numConta).localeCompare(String(b.numConta));
          }));
        })
        .catch(() => setContas([]));
    }, [token, cnpj]);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = React.useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, total: 0, qtd: 0 };
      map[chave].total += parseFloat(it.vlr) || 0;
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    if (!cnpj || !ano) {
      showDefaultModal('error', 'Escola ou ano não selecionado.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchExtratos({ token, cnpj, ano })
      .then(data => {
        setItems(data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar extratos.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }
  function handleNew() {
    // Define data padrão: ano do localStorage + mês/dia atuais
    let dataPadrao = '';
    if (ano) {
      const now = new Date();
      const mes = String(now.getMonth() + 1).padStart(2, '0');
      const dia = String(now.getDate()).padStart(2, '0');
      dataPadrao = `${ano}-${mes}-${dia}`;
    }
    setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', data: dataPadrao, _isEditing: false });
    setShowModal(true);
  }
  // Retorna a data no formato yyyy-MM-dd, sem conversão de timezone
  function toDateInputValue(date) {
    if (!date) return '';
    // Se vier como timestamp (número ou string numérica grande)
    if (!isNaN(date) && String(date).length >= 10) {
      const d = new Date(Number(date));
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Se vier como yyyy-mm-dd ou yyyy-mm-ddTHH:mm:ss
    if (typeof date === 'string' && date.length >= 10) {
      return date.slice(0, 10);
    }
    return '';
  }
  function handleEdit() {
    if (selected.length !== 1) return;
    const s = selected[0];
    setForm({ ...s, data: toIsoDate(s.data), _isEditing: true });
    setShowModal(true);
  }
  function handleSave(e) {
    e.preventDefault();
    // Garante que vlr seja número ou string com ponto
    // Garante que data seja yyyy-MM-dd (sem conversão de timezone)
    const payload = {
      ...form,
      vlr: form.vlr === '' ? null : (typeof form.vlr === 'string' ? form.vlr.replace(',', '.') : form.vlr),
      data: typeof form.data === 'string' ? form.data.slice(0, 10) : ''
    };
    axios.post(`${API_BASE_URL.EXTRATO}/save`, payload, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]);
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar extrato.'));
  }
  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s =>
      axios.delete(`${API_BASE_URL.EXTRATO}/delete/${s.id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      })
    ))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir extrato.'))
    );
  }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  // Importar JSON Extratos
  const fileInputRef = React.useRef();
  function handleImportJsonClick() {
    if (fileInputRef.current) fileInputRef.current.value = null;
    fileInputRef.current?.click();
  }
  async function handleImportJsonFile(e) {
    const file = e.target.files && e.target.files[0];
    console.log('[Extratos] handleImportJsonFile: file', file);
    if (!file) return;
    try {
      const text = await file.text();
      console.log('[Extratos] Arquivo lido:', text.slice(0, 500));
      const json = JSON.parse(text);
      console.log('[Extratos] JSON parseado:', json);
      if (!json.registros || !Array.isArray(json.registros)) throw new Error('JSON inválido');

      // Busca as contas do sistema via API (garante dados atualizados)
      const contaResp = await axios.get(`${API_BASE_URL.CONTA}/findByEscola`, {
        params: { idEscola: cnpj },
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
      const contasApi = Array.isArray(contaResp.data) ? contaResp.data : [];
      console.log('[Extratos] Contas retornadas da API:', contasApi);
      console.log('[Extratos] Lista de numConta disponíveis:', contasApi.map(c => c.numConta));

      function parseDateBRtoISO(dateStr) {
        if (!dateStr) return '';
        if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
        const m = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m) return `${m[3]}-${m[2]}-${m[1]}`;
        return dateStr;
      }
      let ignorados = 0;
      const extratos = json.registros.map(r => {
        const contaObj = contasApi.find(c => c.numConta === r.conta);
        if (!contaObj || !contaObj.idPrograma) {
          console.log('[Extratos] Conta não encontrada ou sem idPrograma:', r.conta, contaObj);
          ignorados++;
          return null;
        }
        // Monta objeto sem o campo 'id' e com todos os campos obrigatórios preenchidos
        const extratoObj = {
          ano: ano || '',
          idEscola: cnpj || '',
          idPrograma: contaObj.idPrograma,
          idConta: r.conta,
          tipo: r.lote || '',
          documento: r.linhaCompleta || '',
          vlr: r.valor ? String(r.valor).replace('.', '').replace(',', '.') : '0.00',
          data: parseDateBRtoISO(r.data) || '',
        };
        // Remove campos vazios ou nulos obrigatórios
        if (!extratoObj.ano || !extratoObj.idEscola || !extratoObj.idPrograma || !extratoObj.idConta || !extratoObj.tipo || !extratoObj.documento || !extratoObj.vlr || !extratoObj.data) {
          console.log('[Extratos] Registro ignorado por campo obrigatório vazio:', extratoObj);
          ignorados++;
          return null;
        }
        console.log('[Extratos] Extrato montado (pronto para envio):', extratoObj);
        return extratoObj;
      }).filter(Boolean);
      console.log('[Extratos] Extratos a importar:', extratos);
      let ok = 0, fail = 0;
      const sucesso = [];
      const falha = [];
      for (const ext of extratos) {
        try {
          const resp = await axios.post(`${API_BASE_URL.EXTRATO}/save`, ext, { headers: { Authorization: `Bearer ${token}` } });
          console.log('[Extratos] Sucesso ao salvar extrato:', ext, 'Resposta backend:', resp && resp.data);
          ok++;
          sucesso.push({ conta: ext.idConta, data: ext.data, valor: ext.vlr });
        } catch (errPost) {
          if (errPost.response) {
            console.log('[Extratos] Falha ao salvar extrato:', ext, 'Status:', errPost.response.status, 'Data:', errPost.response.data);
          } else {
            console.log('[Extratos] Falha ao salvar extrato:', ext, errPost);
          }
          fail++;
          falha.push({ conta: ext.idConta, data: ext.data, valor: ext.vlr });
        }
      }
      setAtualizar(a => !a);
      let msg = `Importação concluída. Sucesso: ${ok}, Falha: ${fail}`;
      if (ignorados > 0) msg += `. Registros ignorados (conta não encontrada): ${ignorados}`;
      if (ok > 0) {
        msg += `\n\nSucesso:`;
        sucesso.forEach(s => {
          msg += `\nConta: ${s.conta} | Data: ${s.data} | Valor: ${Number(s.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
        });
      }
      if (fail > 0) {
        msg += `\n\nFalha:`;
        falha.forEach(f => {
          msg += `\nConta: ${f.conta} | Data: ${f.data} | Valor: ${Number(f.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
        });
      }
      console.log('[Extratos] Fim da importação:', msg);
      showDefaultModal('info', msg);
    } catch (err) {
      console.log('[Extratos] Erro ao importar JSON:', err);
      showDefaultModal('error', 'Erro ao importar JSON: ' + (err.message || ''));
    }
  }
  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="EXTRATOS" icon="bi-journal-text" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <button className="btn btn-info btn-sm botao-igual" type="button" onClick={handleImportJsonClick}><i className="bi bi-upload me-1"></i>Importar Json</button>
            <input type="file" accept="application/json" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImportJsonFile} />

          </Toolbar>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => {
                const programa = programas.find(p => p.id === prog.idPrograma);
                return (
                  <div className="card border-0 shadow-sm" key={prog.idPrograma} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                    <div className="card-body pt-2 pb-1 px-3">
                      <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                        <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.idPrograma}>{programa ? programa.descricao : prog.idPrograma}</span>
                        <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                      </div>
                      <div className="d-flex justify-content-between fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                        <span>Total</span><span className="text-success">{formatCurrency(prog.total)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <TableContainer isLoading={false}>
              <DataTable head={
                  <tr>
                    <th>#</th>
                    <th>Ano</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Documento</th>
                    <th>Valor</th>
                  </tr>
                }>
                <tbody>
                  {displayed.map((item, idx) => {
                    const programa = programas.find(p => p.id === item.idPrograma);
                    return (
                      <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td>
                        <td>{item.ano}</td>
                        <td>{programa ? programa.descricao : item.idPrograma}</td>
                        <td>{item.idConta}</td>
                        <td>{formatDateView(item.data)}</td>
                        <td>{item.tipo}</td>
                        <td>{item.documento}</td>
                        <td className="text-end">{formatCurrency(item.vlr)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 20, background: '#fff', backgroundColor: '#fff', pointerEvents: 'auto', boxShadow: '0 -1px 0 rgba(0,0,0,0.04)' }}>
                  <tr style={{ background: '#fff', height: '2px' }}>
                    <td colSpan="8" style={{ background: '#fff', padding: 0, margin: 0, border: 'none', height: '2px', lineHeight: '2px' }} />
                  </tr>
                  <tr style={{ background: '#fff', height: '25px' }}>
                    <td colSpan="7" className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>Total da página:</td>
                    <td className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>
                      {formatCurrency(displayed.reduce((sum, item) => sum + (parseFloat(item.vlr) || 0), 0))}
                    </td>
                  </tr>
                  <tr style={{ background: '#fff', height: '25px' }}>
                    <td colSpan="7" className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none'  }}>Total geral:</td>
                    <td className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>
                      {formatCurrency(items.reduce((sum, item) => sum + (parseFloat(item.vlr) || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      {/* Modal de cadastro/edição */}
      <ExtratoModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} contas={contas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}


